import { useTypewriter } from "@/hooks/useTypewriter";
import { motion } from "framer-motion";

interface TypewriterTextProps {
  text: string;
  speed?: number;
  delay?: number;
  className?: string;
  highlightWords?: { word: string; className: string }[];
}

export function TypewriterText({
  text,
  speed = 50,
  delay = 0,
  className = "",
  highlightWords = [],
}: TypewriterTextProps) {
  const { displayedText, isComplete } = useTypewriter({ text, speed, delay });

  // Function to apply highlighting to specific words
  const renderHighlightedText = (content: string) => {
    if (highlightWords.length === 0) {
      return content;
    }

    let result: (string | JSX.Element)[] = [content];

    highlightWords.forEach(({ word, className: highlightClass }) => {
      result = result.flatMap((part, partIndex) => {
        if (typeof part !== "string") return part;

        const parts = part.split(word);
        if (parts.length === 1) return part;

        return parts.flatMap((segment, index) => {
          if (index === parts.length - 1) return segment || [];
          return [
            segment,
            <span key={`${word}-${partIndex}-${index}`} className={highlightClass}>
              {word}
            </span>,
          ];
        });
      });
    });

    return result;
  };

  return (
    <span className={`${className} relative block overflow-hidden`} style={{ wordBreak: "break-word" }}>
      {/* Invisible full text to reserve layout space and prevent CLS */}
      <span aria-hidden="true" className="invisible block">
        {renderHighlightedText(text)}
      </span>
      {/* Visible typewriter text overlaid on top - use clip to prevent CLS */}
      <span className="absolute inset-0 text-center" style={{ willChange: "contents", contain: "layout" }}>
        {renderHighlightedText(displayedText)}
        {!isComplete && (
          <motion.span
            animate={{ opacity: [1, 0, 1] }}
            transition={{ duration: 0.8, repeat: Infinity }}
            className="inline-block w-[2px] h-[1em] bg-primary ml-1 align-middle"
          />
        )}
      </span>
    </span>
  );
}
